'use client'

import React from 'react'
import { motion, type Variant, type UseInViewOptions } from 'framer-motion'

type AnimationVariant = 'fadeUp' | 'fadeDown' | 'fadeLeft' | 'fadeRight' | 'fade' | 'scale' | 'none'
type ElementType =
  | 'div'
  | 'section'
  | 'article'
  | 'span'
  | 'main'
  | 'header'
  | 'footer'
  | 'aside'
  | 'nav'

interface ScrollAnimationProps {
  children: React.ReactNode
  variant?: AnimationVariant
  delay?: number
  duration?: number
  className?: string
  once?: boolean
  amount?: number | 'some' | 'all'
  as?: ElementType
  /** Set margin around the root to trigger earlier/later. e.g., "-100px" triggers 100px before entering viewport */
  margin?: UseInViewOptions['margin']
}

const variants: Record<AnimationVariant, { hidden: Variant; visible: Variant }> = {
  fadeUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -40 },
    visible: { opacity: 1, y: 0 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 },
  },
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
  },
  none: {
    hidden: {},
    visible: {},
  },
}

export function ScrollAnimation({
  children,
  variant = 'fadeUp',
  delay = 0,
  duration = 0.4,
  className,
  once = true,
  amount = 0.15, // Reduced from 0.25 for better trigger reliability
  as = 'div',
  margin,
}: ScrollAnimationProps) {
  const MotionComponent = motion[as]

  return (
    <MotionComponent
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount, margin }}
      variants={variants[variant]}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1], // cubic-bezier easing
      }}
      className={className}
    >
      {children}
    </MotionComponent>
  )
}

// Staggered children animation wrapper
interface StaggerContainerProps {
  children: React.ReactNode
  className?: string
  staggerDelay?: number
  once?: boolean
  amount?: number | 'some' | 'all'
  margin?: UseInViewOptions['margin']
}

export function StaggerContainer({
  children,
  className,
  staggerDelay = 0.1,
  once = true,
  amount = 0.15,
  margin,
}: StaggerContainerProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount, margin }}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

// Individual stagger item
interface StaggerItemProps {
  children: React.ReactNode
  className?: string
  variant?: AnimationVariant
  duration?: number
}

export function StaggerItem({
  children,
  className,
  variant = 'fadeUp',
  duration = 0.5,
}: StaggerItemProps) {
  return (
    <motion.div
      variants={variants[variant]}
      transition={{
        duration,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}
